import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  CheckCircle2,
  Loader2,
  CreditCard,
  Shield,
  ArrowRight,
  AlertCircle,
  Stethoscope,
  Tag,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPage = () => {
  const { user, token, refreshUser, hasActiveSubscription } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingStatus(false);
      toast.info('Payment status check timed out. Please refresh to check again.');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/subscription/status/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.payment_status === 'paid') {
        await refreshUser();
        setCheckingStatus(false);
        toast.success('Subscription activated! Welcome to Trac Jobs Alert!');
        setTimeout(() => navigate('/dashboard'), 1500);
        return;
      }

      if (response.data.status === 'expired') {
        setCheckingStatus(false);
        toast.error('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingStatus(false);
      toast.error('Error checking payment status');
    }
  }, [token, refreshUser, navigate]);

  useEffect(() => {
    if (sessionId && !hasActiveSubscription) {
      setCheckingStatus(true);
      pollPaymentStatus(sessionId);
    }
  }, [sessionId, hasActiveSubscription, pollPaymentStatus]);

  useEffect(() => {
    if (hasActiveSubscription && !sessionId) {
      navigate('/dashboard');
    }
  }, [hasActiveSubscription, sessionId, navigate]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API_URL}/api/subscription/create-checkout`,
        { 
          origin_url: originUrl,
          coupon_code: appliedCoupon?.code || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/coupon/validate`,
        { code: couponCode.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAppliedCoupon(response.data);
      setCouponCode('');
      toast.success(`Coupon applied! ${response.data.percentage}% off`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
  };

  const features = [
    'Unlimited email notifications',
    'Filter by specialty & grade',
    'All UK regions covered',
    'Real-time job matching',
    'Cancel anytime',
    'Track up to 5 jobs simultaneously',
  ];

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#00A651] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Processing your payment...</h2>
          <p className="text-slate-600">Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#00A651] flex items-center justify-center">
              <Stethoscope className="w-7 h-7 text-white" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Activate Your Subscription
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            Get instant access to all healthcare job alerts across the UK
          </p>
        </div>

        {/* Subscription Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-[#00A651] p-6 text-white text-center">
            <p className="text-sm font-medium text-white/80 mb-2">Monthly Subscription</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-5xl font-bold">£9.99</span>
              <span className="text-white/80">/month</span>
            </div>
          </div>

          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#00A651] flex-shrink-0" />
                  <span className="text-slate-700">{feature}</span>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              {/* Coupon Code Section */}
              <div className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Have a coupon code?</span>
                </div>
                
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div>
                      <span className="font-medium text-green-800">{appliedCoupon.code}</span>
                      <span className="text-green-600 ml-2">({appliedCoupon.percentage}% off)</span>
                    </div>
                    <button 
                      onClick={removeCoupon}
                      className="text-green-600 hover:text-green-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 uppercase"
                      data-testid="coupon-input"
                    />
                    <Button
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      variant="outline"
                      className="px-4"
                      data-testid="apply-coupon-btn"
                    >
                      {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                )}
                
                {appliedCoupon && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Original price:</span>
                      <span className="text-slate-500 line-through">£9.99</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium mt-1">
                      <span className="text-slate-700">You pay:</span>
                      <span className="text-green-600">£{(9.99 * (1 - appliedCoupon.percentage / 100)).toFixed(2)}/month</span>
                    </div>
                  </div>
                )}
              </div>

              <Button
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full h-14 bg-[#00A651] hover:bg-[#008C44] text-white text-lg rounded-full btn-hover-lift"
                data-testid="subscribe-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Redirecting to payment...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Subscribe Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                <Shield className="w-4 h-4" />
                <span>Secure payment powered by Stripe</span>
              </div>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-100">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Subscription Required</h3>
              <p className="text-blue-700 text-sm">
                An active subscription is required to create job alerts and receive notifications.
                You can cancel your subscription at any time from your settings.
              </p>
            </div>
          </div>
        </div>

        {/* User Info */}
        {user && (
          <div className="mt-6 text-center text-sm text-slate-500">
            Subscribing as <span className="font-medium text-slate-700">{user.email}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
